import { Link, useParams } from "react-router-dom";
import { useMemo } from "react";
import SubsWall, { STYLE_PALETTES, type SubTile, type TileStyle } from "../components/SubsWall";
import "./ProfileV4.css";

function mockProfile(handle: string) {
  const display = handle.charAt(0).toUpperCase() + handle.slice(1);
  const seed: Array<Partial<SubTile> & { name: string; message: string; monthlyCents: number; style: TileStyle }> = [
    { name: "KAIROS",       message: "first one in. shipping forever.",          monthlyCents: 500,   style: "spray" },
    { name: "ZINC",         message: "$5/mo for a forever spot.",                monthlyCents: 500,   style: "chalk" },
    { name: "pulse",        message: "tell my mom I made it on the wall",        monthlyCents: 500,   style: "marker" },
    { name: "lo-fi-ghost",  message: "playing your VOD on loop while I code",    monthlyCents: 1000,  style: "neon", blink: true },
    { name: "MOTH",         message: "I came for the lore, stayed for the bits", monthlyCents: 1000,  style: "neon", blink: true },
    { name: "FRACTAL",      message: "the soundtrack saved my run",              monthlyCents: 2500,  style: "neon", blink: true, anniversaryMonths: 7 },
    { name: "VEXLINE",      message: "watching since the API days",              monthlyCents: 5000,  style: "carved", badge: "shield" },
    { name: "BIGSHOT",      message: "yes I bought the biggest tile. fight me.", monthlyCents: 10000, style: "neon", blink: true, badge: "crown" },
    { name: "OMEN",         message: "ascendant arc commencing",                 monthlyCents: 10000, style: "carved", badge: "lightning" },
    { name: "AURORA",       message: "day-one founder. forever.",                monthlyCents: 25000, style: "neon", blink: true, badge: "star", founderNumber: 1, anniversaryMonths: 12 },
  ];
  const tiles: SubTile[] = seed.map((s, i) => ({
    id: `t${i}`,
    name: s.name,
    message: s.message,
    style: s.style,
    color: STYLE_PALETTES[s.style][(i + 2) % 8],
    xPct: 12 + ((i * 71) % 76),
    yPct: 18 + ((i * 47) % 68),
    monthlyCents: s.monthlyCents,
    anniversaryMonths: s.anniversaryMonths ?? 0,
    founderNumber: s.founderNumber ?? null,
    boostUntil: null,
    fadeStartedAt: null,
    badge: s.badge ?? null,
    blink: s.blink ?? false,
  }));
  const mrr = tiles.reduce((s, t) => s + t.monthlyCents / 100, 0);
  return {
    handle,
    display_name: display,
    bio: "Long-form essays and interviews about the craft of building. Two videos a month, no filler.",
    youtube_handle: `@${handle}`,
    youtube_url: `https://youtube.com/@${handle}`,
    member_since: "June 2023",
    supporter_count: tiles.length,
    monthly_recurring: mrr,
    tiles,
  };
}

export default function ProfileV4(): JSX.Element {
  const { handle = "demo" } = useParams<{ handle: string }>();
  const profile = useMemo(() => mockProfile(handle), [handle]);

  return (
    <div className="pp-page pv4">
      <nav className="pv4-nav pp-container">
        <Link to="/" className="pv4-brand">
          <span className="pv4-brand-glyph"></span> SubsWall
        </Link>
        <div className="pv4-nav-right">
          <Link to="/login">Sign in</Link>
          <Link to={`/${handle}/sign`} className="pp-btn pp-btn-primary pp-btn-sm">
            Sign the wall →
          </Link>
        </div>
      </nav>

      <header className="pv4-mast pp-container">
        <div className="pv4-mast-left">
          <div className="pv4-av">{profile.display_name[0]}</div>
          <div>
            <div className="pp-eyebrow">Creator wall</div>
            <h1 className="pp-display pv4-h1">{profile.display_name}</h1>
            <div className="pv4-channel">
              <a href={profile.youtube_url} target="_blank" rel="noopener noreferrer">
                {profile.youtube_handle} on YouTube ↗
              </a>
            </div>
          </div>
        </div>
        <div className="pv4-mast-stats">
          <div className="pv4-stat">
            <div className="pp-eyebrow">Supporters</div>
            <div className="pp-mono pv4-stat-num">{profile.supporter_count}</div>
          </div>
          <div className="pv4-stat">
            <div className="pp-eyebrow">Monthly</div>
            <div className="pp-mono pv4-stat-num">${profile.monthly_recurring.toLocaleString()}</div>
          </div>
          <div className="pv4-stat">
            <div className="pp-eyebrow">Since</div>
            <div className="pp-mono pv4-stat-sub">{profile.member_since}</div>
          </div>
        </div>
      </header>

      <p className="pv4-bio pp-container pp-lead">{profile.bio}</p>

      <section className="pv4-wall pp-container">
        <div className="pv4-wall-frame">
          <SubsWall tiles={profile.tiles} theme="glow" />
        </div>
      </section>

      <section className="pv4-cta pp-container">
        <div>
          <h2 className="pp-display pv4-h2">Sign the wall.</h2>
          <p className="pp-lead">Pick an amount, write a line, drop your tile. Stay as long as you support.</p>
        </div>
        <Link to={`/${handle}/sign`} className="pp-btn pp-btn-primary pp-btn-lg">
          Become a supporter →
        </Link>
      </section>

      <section className="pv4-list pp-container">
        <div className="pp-eyebrow">Standing with {profile.display_name}</div>
        <h2 className="pp-display pv4-h2">Recent supporters.</h2>
        <ul className="pv4-supporters">
          {profile.tiles.slice(0, 10).map((t) => (
            <li key={t.id}>
              <div className="pv4-sup-name">{t.name}</div>
              <div className="pv4-sup-msg">"{t.message}"</div>
              <div className="pv4-sup-amt pp-mono">${(t.monthlyCents / 100).toFixed(0)}/mo</div>
            </li>
          ))}
        </ul>
      </section>

      <footer className="pv4-footer">
        <div className="pp-container pv4-footer-inner">
          <div className="pv4-brand">
            <span className="pv4-brand-glyph"></span> SubsWall
          </div>
          <Link to="/">Home</Link>
        </div>
      </footer>
    </div>
  );
}
